<template>
    <v-autocomplete
        ref="autocomplete"
        v-model="__modelValue"
        v-model:search="__search"
        v-model:menu="menu"
        :items="_items"
        :loading="loading"
        :custom-filter="() => true"
        menu-icon=""
        :menu-props="menuProps"
        hide-details
        return-object
        hide-no-data
        persistent-clear
        clear-icon="mdi-close"
        clearable
        v-bind="inputProps"
        class="place-autocomplete"
        @focus="selectAll"
        @click:clear="clear"
    >
        <template #item="{ props: listItemProps, item }">
            <v-list-item
                v-if="isGroupHeader(item.raw)"
                :data-id="item.raw.id"
                :title="item.title"
                class="place-autocomplete__item-header"
            />
            <v-list-item v-else :data-id="item.raw.id" v-bind="listItemProps" :title="item.title" class="place-autocomplete__item">
                <template v-if="item.raw.icon" #prepend>
                    <div v-if="isArray(item.raw.icon)" class="d-flex flex-column">
                        <v-sheet
                            v-for="icon in item.raw.icon"
                            :key="icon"
                            width="24"
                            height="16"
                            class="flex-shrink-0 d-flex align-center justify-center"
                        >
                            <v-icon :icon="icon" size="24" />
                        </v-sheet>
                    </div>
                    <v-sheet v-else width="24" height="16" class="flex-shrink-0 d-flex align-center justify-center">
                        <quay-signage v-if="isQuay(item.raw)" :icon="item.raw.icon" :color="iconColor" icon-size="22" />
                        <v-icon v-else :icon="item.raw.icon" size="24" :color="iconColor" />
                    </v-sheet>
                </template>
                <template v-if="hasSubtitle(item.raw)" #subtitle>
                    {{ item.raw.value.subtitle }}
                </template>
                <template v-if="hasDistance(item.raw) && !hideDistance" #append>
                    {{ humanizeDistance(item.raw.value.distance) }}
                </template>
            </v-list-item>
        </template>
        <template v-if="isMyLocation" #selection="{ item }">
            <div class="v-autocomplete__selection-text-container">
                <div class="v-autocomplete__selection-text mb-1">
                    {{ t('places.resources.yourPosition', 'Your location') }}
                </div>
                <div class="v-autocomplete__selection-text text-body-2 font-weight-light">
                    {{ item.title }}
                </div>
            </div>
        </template>
        <template #prepend-inner>
            <div v-if="showPlaceTypeIcon" class="mr-2">
                <div v-if="__modelValue">
                    <v-sheet
                        v-if="isMyLocation"
                        width="24"
                        height="32"
                        color="transparent"
                        class="flex-shrink-0 d-flex align-center justify-center"
                    >
                        <v-icon icon="mdi-map-marker" size="24" :color="iconColor" />
                    </v-sheet>
                    <div v-else-if="isArray(__modelValue.icon)" class="d-flex flex-column">
                        <v-sheet
                            v-for="icon in __modelValue.icon"
                            :key="icon"
                            width="24"
                            height="16"
                            color="transparent"
                            class="flex-shrink-0 d-flex align-center justify-center"
                        >
                            <v-icon :icon="icon" size="24" :color="iconColor" />
                        </v-sheet>
                    </div>
                    <v-sheet v-else width="24" height="32" color="transparent" class="flex-shrink-0 d-flex align-center justify-center">
                        <quay-signage v-if="isQuay(__modelValue)" :icon="__modelValue.icon" :color="iconColor" icon-size="22" />
                        <v-icon v-else :icon="__modelValue.icon" :color="iconColor" />
                    </v-sheet>
                </div>
                <v-sheet v-else width="24" height="32" color="transparent">
                    <v-icon icon="mdi-magnify" size="32" :color="iconColor" class="ml-n1" />
                </v-sheet>
            </div>
        </template>
    </v-autocomplete>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { type VAutocomplete } from 'vuetify/components';
import { useKolumbusIcons, useViewportSize } from '@/features/common/composables';
import {
    type ListItem,
    type ListItemGeolocation,
    type ListItemGroupHeader,
    type ListItemPlace,
    type ListItemSearchHistory,
    type SearchHistoryItem
} from '@/features/places/models';
import { type PlaceAutocompleteModelDto, PlaceType } from '@/types/webapi';
import { refThrottled, useElementSize } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { isArray, isObject } from 'lodash-es';
import { humanizeDistance } from '@/helpers';
import { QuaySignage } from '@geta/kolumbus-frontend/components';
// @ts-ignore
import { useTextColor } from 'vuetify/lib/composables/color.mjs';
import { ComponentProps } from 'vue-component-type-helpers';
import { useDisplay } from 'vuetify';

interface Props {
    /** The selected place model value */
    modelValue: PlaceAutocompleteModelDto | null;

    /** List of place suggestions to display in the autocomplete */
    items?: PlaceAutocompleteModelDto[];

    /** List of previously selected places */
    pastItems?: PlaceAutocompleteModelDto[];

    /** List of places from search history */
    historyItems?: SearchHistoryItem[];

    /** Whether the component is in a loading state */
    loading?: boolean;

    /** Current search input value */
    search?: string;

    /** Whether to include "My Location" option in the suggestions */
    includeMyLocation?: boolean;

    /** Whether the current selection is user's location */
    isMyLocation?: boolean;

    /** Whether to display place type icons in the suggestions */
    showPlaceTypeIcon?: boolean;

    /** Controls the compactness of the component
     * @default null
     */
    density?: null | 'default' | 'comfortable' | 'compact';

    /** Visual style variant of the input field
     * @default 'filled'
     */
    variant?: 'filled' | 'underlined' | 'outlined' | 'plain' | 'solo' | 'solo-inverted' | 'solo-filled';

    /** Primary color for active states and focus */
    color?: string;

    /** Base color for inactive states */
    baseColor?: string;

    /** Background color of the input field */
    bgColor?: string;

    /** CSS class to apply when the component is active */
    activeClass?: string;

    /** Whether to hide the distance */
    hideDistance?: boolean;
}

interface Emits {
    (e: 'select:geolocation'): void;
    (e: 'select:historyItem', value: SearchHistoryItem): void;
    (e: 'clear'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();
const { mobile: isMobile } = useDisplay();
const { height } = useViewportSize();

const menu = defineModel<boolean>('menu');
const _modelValue = defineModel<PlaceAutocompleteModelDto | null>('modelValue', { required: true });
const __modelValue = computed<ListItem | null>({
    get() {
        if (!_modelValue.value) return null;

        return {
            id: _modelValue.value.id,
            title: _modelValue.value.title,
            type: 'item',
            icon: getPlaceIcon(_modelValue.value),
            value: _modelValue.value
        };
    },
    set(item: ListItem | null) {
        _isMyLocation.value = false;

        if (!item) {
            _modelValue.value = null;
            return;
        }

        if (isListItemGeolocation(item)) {
            _isMyLocation.value = true;
            emit('select:geolocation');
        } else if (isListItemSearchHistory(item)) {
            emit('select:historyItem', item.value);
        } else {
            _modelValue.value = (item as ListItemPlace).value;
        }
    }
});
const _isMyLocation = defineModel<boolean>('isMyLocation');
const _search = defineModel<string>('search');
const __search = ref('');
const __debouncedSearch = refThrottled(__search, 500, true, false);
const autocomplete = ref<VAutocomplete | undefined>();
const { width } = useElementSize(autocomplete.value?.$el, undefined, { box: 'border-box' });
const { textColorClasses } = useTextColor(computed(() => props.color));
const iconColor = 'secondary';
const inputProps = computed(() => ({
    density: props.density,
    variant: props.variant,
    color: props.color,
    baseColor: props.baseColor,
    bgColor: props.bgColor,
    class: [menu.value ? props.activeClass : ''].filter(Boolean)
}));
// if mobile, set to viewport height - (header + input field container) OR 300px, whichever is smaller
const menuProps = computed<ComponentProps<typeof VAutocomplete>['menuProps']>(() => ({
    target: 'parent',
    width: width.value,
    maxWidth: width.value,
    minHeight: 40,
    maxHeight: isMobile.value ? Math.max(0, Math.min(height.value - 150, 300)) : 300,
    contentClass: ['place-autocomplete__dropdown', textColorClasses.value],
    offset: [4, 0],
    scrollStrategy: 'block'
}));

const { t } = useI18n();

const _items = computed<Partial<ListItem>[]>(() => {
    let result: ListItem[] = [];

    if (props.includeMyLocation) {
        result = [
            {
                id: 'myLocationListItem',
                type: 'geolocation',
                title: t('places.resources.yourPosition', 'Your location'),
                icon: 'mdi-crosshairs-gps'
            } satisfies ListItemGeolocation
        ];
    }

    if (props.items) {
        result = [
            ...result,
            ...props.items.map<ListItemPlace>(item => ({
                id: item.id,
                type: 'item',
                title: item.title,
                value: item,
                icon: getPlaceIcon(item)
            }))
        ];
    }

    if (isArray(props.pastItems) && props.pastItems.length && !__search.value.length) {
        result = [
            ...result,
            ...props.pastItems.map<ListItemPlace>(item => ({
                id: item.id,
                type: 'pastItem',
                title: item.title,
                value: item,
                icon: getPlaceIcon(item)
            }))
        ];
    }

    if (isArray(props.historyItems) && props.historyItems.length && !__search.value.length) {
        result = [
            ...result,
            ...props.historyItems.map<ListItemSearchHistory>(item => ({
                id: item.id,
                type: 'historyItem',
                title: `${item.from.title}${item.to && ` - ${item.to.title}`}`,
                value: item,
                icon: 'mdi-history'
            }))
        ];
    }

    return result;
});

const { getTransportModeIcon } = useKolumbusIcons();

function clear() {
    __search.value = '';
    _modelValue.value = null;
    emit('clear');
}

function selectAll() {
    // select all text when field is focused
    nextTick(() => autocomplete.value?.$el.querySelector('input').select());
}

function blur() {
    nextTick(() => autocomplete.value?.$el.querySelector('input').blur());
}

function isGroupHeader(value: unknown): value is ListItemGroupHeader {
    return isObject(value) && 'type' in value && value.type === 'header';
}

function isListItemGeolocation(value: unknown): value is ListItemGeolocation {
    return isObject(value) && 'type' in value && value.type === 'geolocation';
}

function isListItemSearchHistory(value: unknown): value is ListItemSearchHistory {
    return isObject(value) && 'type' in value && value.type === 'historyItem';
}

function hasDistance(raw: unknown): raw is { value: { distance: number } } {
    return isObject(raw) && 'value' in raw && isObject(raw.value) && 'distance' in raw.value && typeof raw.value.distance === 'number';
}

function hasSubtitle(raw: unknown): raw is { value: { subtitle: string } } {
    return isObject(raw) && 'value' in raw && isObject(raw.value) && 'subtitle' in raw.value && typeof raw.value.subtitle === 'string';
}

function isQuay(raw: unknown): boolean {
    return isObject(raw) && 'value' in raw && isObject(raw.value) && 'type' in raw.value && raw.value.type === PlaceType.TransitStation;
}

function getPlaceIcon(place: PlaceAutocompleteModelDto): string | string[] | undefined {
    if (place.type === PlaceType.Point) return 'mdi-map-marker';

    if (place.type === PlaceType.TransitStation) {
        if (!place.transitInfo) return undefined;

        // sort the transitInfo, first by transportMode, then by stopPlaceType
        const sortedTransitInfo = place.transitInfo.sort((a, b) => {
            if (a.transportMode === b.transportMode) {
                return a.stopPlaceType - b.stopPlaceType;
            }
            return a.transportMode - b.transportMode;
        });

        // get the icons
        const icons = sortedTransitInfo.map(ti => getTransportModeIcon(ti)).filter((icon): icon is string => typeof icon === 'string');

        // restrict to 2 icons
        const limitedIcons = icons.slice(0, 2);

        // if there are 2 icons, return an array, otherwise return the first icon
        return limitedIcons.length > 1 ? limitedIcons : limitedIcons[0];
    }

    return undefined;
}

watch(
    _modelValue,
    value => {
        if (value) blur();
    },
    { deep: true }
);

watch(__debouncedSearch, (value, oldValue) => {
    if (value === oldValue) return;

    _search.value = value || '';
});
</script>

<style lang="scss">
@use 'vuetify/settings' as *;

.place-autocomplete {
    --v-input-control-height: 42px;
    scroll-margin-top: 16px;

    .v-field {
        --v-field-input-padding-top: 8px;
        --v-field-input-padding-bottom: 8px;

        &.v-field--prepended {
            --v-field-padding-start: 0;
        }

        &.v-field--appended {
            --v-field-padding-end: 0;
            padding-inline-end: 4px;
        }
    }

    &__item-header,
    &__item {
        height: 56px;
    }

    &__item-header .v-list-item-title {
        font-weight: 400;
    }

    &__item {
        .v-list-item__prepend {
            width: 32px;
            justify-content: center;
        }

        .v-list-item__prepend > .v-icon,
        .v-sheet {
            opacity: 1;
            margin-inline-end: 16px;
        }
    }

    &__dropdown {
        border: 1px solid currentColor;

        .v-list {
            padding-top: 0;
            padding-bottom: 0;
        }

        .v-list-item {
            border-bottom: 1px solid var(--v-color-grey-lighten-1);

            &:first-of-type {
                border-top: 1px solid var(--v-color-grey-lighten-1);
            }
        }
    }

    .v-autocomplete {
        &__selection {
            font-weight: 500;

            &-text {
                line-height: 1.1;
            }
        }

        &--active-menu .v-field__append-inner > .v-icon {
            transform: none;
        }
    }

    .v-field--variant-outlined {
        .v-field__outline {
            --v-field-border-opacity: 1 !important;
        }

        &.v-field--focused .v-field__outline {
            --v-field-border-width: 1px;
        }
    }

    .v-field {
        font-size: calc(16px / $font-size-root) * 1rem;

        &:not(.v-field--focused) .v-field__append-inner > .v-icon {
            opacity: 0;
        }
    }

    .v-field__input input {
        font-weight: 500;

        &::placeholder {
            color: var(--v-color-grey-darken-2);
            font-style: italic;
            font-weight: 400;
            opacity: 1;
        }
    }

    .v-field__loader {
        color: rgb(var(--v-theme-primary));
    }
}
</style>
